<template lang="pug">
.room-schedule-tab
  .mb-3
  .d-flex.justify-content-start.align-items-center.mb-2
    .d-flex.justify-content-start.align-items-center
      span.current-date {{ year }}年{{ month }}月
      HelpIcon.mr-2(popoverId="other-office-roomschedule-help" title="凡例")
        template(v-slot:text)
          span
            span.can 〇
            span ：空床有り
          br
          span
            span.cant ×
            span ：空床無し
          br
          span
            span.blank-space 空欄
            span ：未公開
      .warning-message(style="font-size:14px;") 施設を選択するとメッセージ作成タブからメッセージを送信できます。
    .d-flex.justify-content-end.flex-fill
      button.btn.move-month-btn.mr-2(type="button" :class="{ disable: disablePreviousMonth }" :disabled="disablePreviousMonth" @click="onPreviousMonth") ＜ 前の月
      button.btn.move-month-btn.mr-2(type="button" @click="onCurrentMonth") 今月
      button.btn.move-month-btn(type="button" @click="onNextMonth") 次の月 ＞
  RoomScheduleTable(
    v-model="otherOfficeRoomSchedules"
    :fields="fields"
    :isLoading="isLoading"
    :filter="filter"
    :allSelect.sync="allSelect"
    @onAllSelect="changeAllSelect"
    @changeFacilityCheck="changeFacilityCheck"
  )
  .table-options.d-flex.flex-row.align-items-center.flex-wrap
    //- 概要検索
    .basic-filters.d-flex.flex-column.flex-wrap.p-2
      //- 地域コード
      .basic-filter
        .d-flex.flex-row.align-items-center
          span.table-option-text.font-weight-bold.text-nowrap 都道府県:
          b-form-select(id="filter-prefecture" v-model="filter.prefecture" :options="_prefectureOptions" size="sm" style="width: 120px; max-width: 120px;")
        .d-flex.flex-column.flex-wrap.mt-2(v-if="filter.prefecture")
          span.table-option-text.font-weight-bold.mb-2 市区町村:
          b-form-checkbox-group(v-model="filter.municipalities[filter.prefecture]")
            b-form-checkbox.mr-4.mb-2(
              v-for="municipality in _municipalities" :key="municipality.municipalityCode" :value="municipality.municipalityCode" size="sm") {{ municipality.name }}
      //- 施設種別
      .basic-filter
        .d-flex.flex-row.align-items-center
          span.table-option-text.font-weight-bold.text-nowrap 施設種別:
          b-form-select(id="filter-facility-category" v-model="filter.facilityCategory" :options="_facilityCategoryOptions" size="sm" style="width: 300px; max-width: 300px;")
      //- 介護度（複数選択可能）
      .basic-filter
        .d-flex.flex-column.flex-wrap
          span.table-option-text.font-weight-bold.mb-2 介護度:
          div
            b-form-checkbox.mr-4.mb-2(v-model="filter.careLevels.careLevel1000" inline) 自立
            b-form-checkbox.mr-4.mb-2(v-model="filter.careLevels.careLevel1010" inline) 申請中
            b-form-checkbox.mr-4.mb-2(v-model="filter.careLevels.careLevel2010" inline) 要支援１
            b-form-checkbox.mr-4.mb-2(v-model="filter.careLevels.careLevel2020" inline) 要支援２
            b-form-checkbox.mr-4.mb-2(v-model="filter.careLevels.careLevel3010" inline) 要介護１
            b-form-checkbox.mr-4.mb-2(v-model="filter.careLevels.careLevel3020" inline) 要介護２
            b-form-checkbox.mr-4.mb-2(v-model="filter.careLevels.careLevel3030" inline) 要介護３
            b-form-checkbox.mr-4.mb-2(v-model="filter.careLevels.careLevel3040" inline) 要介護４
            b-form-checkbox.mr-4.mb-2(v-model="filter.careLevels.careLevel3050" inline) 要介護５
      //- 空床有り施設のみ
      .basic-filter
        .d-flex.flex-row.align-items-center.flex-wrap(style="min-height: 31px;")
          span.table-option-text.font-weight-bold.text-nowrap 空床:
          b-form-checkbox.mr-2.ml-2(v-model="filter.onlyBlankRoom.use" inline) 空床有り施設のみ
          .d-flex.flex-row.align-items-center.flex-wrap(v-if="filter.onlyBlankRoom.use")
            span.table-option-text.font-weight-bold.text-nowrap.ml-3 日付:
            b-form-input.ml-2.only-blank-room-date(v-model="onlyBlankRoomDate" @click="showDatePickerModal = true;" type="text" size="sm" readonly)

  DatePickerModal(v-model="showDatePickerModal" :date.sync="filter.onlyBlankRoom.date" :min-date="today" @ok="inputOnlyBlankRoomDate")
</template>

<script lang="ts">
import { Component, Inject, Vue, Watch, Prop } from "vue-property-decorator";
import { userStore, regionsStore } from "@/store/store";

import OtherOfficeRoomSchedulesService, { OtherOfficeRoomSchedulesGetModel } from "@/commons/services/OtherOfficeRoomSchedules";
import { RegionMaster } from "@/commons/services/RegionMasters";
import { FacilityCategory } from "@/commons/services/Facilities";

import RoomScheduleTable, { FilterSetting, RoomScheduleTableModel } from "@/pc/containers/otherOfficeRoomSchedules/RoomScheduleTable.vue";
import HelpIcon from "@/pc/basics/HelpIcon.vue";
import DatePickerModal from "@/pc/basics/DatePickerModal.vue";

/**
 * ### 仮押さえ／転床一覧タブコンポーネント
 */
@Component({
  components: {
    HelpIcon,
    RoomScheduleTable,
    DatePickerModal
  }
})
export default class RoomScheduleTab extends Vue {
  /** 待機者ラベルサービス */
  @Inject("OtherOfficeRoomSchedulesService") otherOfficeRoomSchedulesService!: OtherOfficeRoomSchedulesService;

  /** 空床情報タブで選択し、受入リクエストタブで送信対象となる施設情報 */
  @Prop()
  selectedFacilities!: Array<{ facilityId: number; facilityName: string }>;

  /** 全て選択列のチェック */
  allSelect: boolean = false;

  /** フィルタ */
  filter: FilterSetting = {
    /** 地域（都道府県） */
    prefecture: "",
    /** 地域（市区町村） */
    municipalities: {} as { [key: string]: string[] }, // 市区町村の選択状態を管理
    /** 施設種別 */
    facilityCategory: "",
    /** 介護度（複数選択可能） */
    careLevels: {
      /** 自立 */
      careLevel1000: false,
      /** 申請中 */
      careLevel1010: false,
      /** 要支援１ */
      careLevel2010: false,
      /** 要支援２ */
      careLevel2020: false,
      /** 要介護1 */
      careLevel3010: false,
      /** 要介護2 */
      careLevel3020: false,
      /** 要介護3 */
      careLevel3030: false,
      /** 要介護4 */
      careLevel3040: false,
      /** 要介護5 */
      careLevel3050: false
    },
    /** 空床有り施設のみ */
    onlyBlankRoom: {
      /** 使用可否 */
      use: false,
      /** 日時 */
      date: ""
    }
  };
  /** 空床有り施設のみの日付入力の一時格納用の日付 */
  onlyBlankRoomDate: string = "";
  /** 空床有り施設のみの日付入力モーダル表示可否 */
  showDatePickerModal: boolean = false;
  /** 今日の日付 */
  today: Date = new Date();

  /** テーブル列名 */
  fields: Array<string> = [];
  /** テーブル各行の情報 */
  otherOfficeRoomSchedules: Array<RoomScheduleTableModel> = [];
  /** 読込み中か否か */
  isLoading: boolean = true;

  /** 表示対象年 */
  year: number = new Date().getFullYear();
  /** 表示対象月 */
  month: number = new Date().getMonth() + 1;

  /** エラーメッセージ */
  errorMessage: string = "";

  /**
   * ### 前の月ボタンの非活性か否かを返す
   *
   * @return 非活性：true、活性：true
   */
  get disablePreviousMonth(): boolean {
    const today: Date = new Date();
    const currentDay: Date = new Date(this.year, this.month - 1); // monthは0始まりなので-1して使用する

    // 年が未来であれば非活性にならない
    if (today.getFullYear() < currentDay.getFullYear()) {
      return false;
    }
    // 年が同じであればかつ、月が未来であれば非活性にならない
    else if (today.getFullYear() == currentDay.getFullYear() && today.getMonth() < currentDay.getMonth()) {
      return false;
    }
    // その他であれば非活性(年が過去or同じ年かつ同じ月以下)
    else {
      return true;
    }
  }

  /** 都道府県選択肢 */
  get _prefectureOptions(): Array<{ value: string; text: string }> {
    if (!regionsStore) {
      return [];
    }

    const options: Array<{ value: string; text: string }> = [];
    options.push({ value: "", text: "全国" });
    for (const region of regionsStore.regions) {
      options.push({ value: region.prefecture.prefectureCode, text: region.prefecture.name });
    }

    return options;
  }

  /** 施設種別選択肢 */
  get _facilityCategoryOptions(): Array<{ value: "" | FacilityCategory; text: string }> {
    return [
      { value: "", text: "全て" },
      { value: FacilityCategory.KAIGO_YUURYOU, text: "介護付有料老人ホーム" },
      { value: FacilityCategory.JYUUTAKU_YUURYOU, text: "住宅型有料老人ホーム" },
      { value: FacilityCategory.SERVICE_JYUUTAKU, text: "サービス付き高齢者向け住宅" },
      { value: FacilityCategory.CONDOMINIUMS, text: "シニア向け分譲マンション" },
      { value: FacilityCategory.GROUP_HOME, text: "グループホーム" },
      { value: FacilityCategory.CARE_HOUSE, text: "ケアハウス" },
      { value: FacilityCategory.TOKUYO, text: "特別養護老人ホーム（特養）" },
      { value: FacilityCategory.KAIGO_HOKEN, text: "介護老人保健施設（老健）" },
      { value: FacilityCategory.MEDICAL_CENTER, text: "介護医療院" },
      { value: FacilityCategory.OTHER, text: "その他" }
    ];
  }

  /**
   * ### 都道府県の変更を監視する
   * 市区町村のチェックをリセットする
   */
  @Watch("filter.prefecture")
  onPrefectureChanged(): void {
    this.filter.municipalities = {};
  }

  /** 選択されている都道府県の市区町村 */
  get _municipalities(): Array<RegionMaster> {
    for (const region of regionsStore.regions) {
      if (region.prefecture.prefectureCode == this.filter.prefecture) {
        return region.municipalities;
      }
    }
    return [];
  }

  created(): void {
    if (this.filter.prefecture && !this.filter.municipalities[this.filter.prefecture]) {
      this.$set(this.filter.municipalities, this.filter.prefecture, []);
    }
  }

  /**
   * ### vue lifecycle mounted
   */
  async mounted(): Promise<void> {
    // 空床あり施設のみ表示の日付入力を初期化する
    this.filter.onlyBlankRoom.date = this.$dateUtil.convertDateToString(new Date(), "yyyy-mm-dd");
    this.onlyBlankRoomDate = this.$dateUtil.convertDateToString(new Date(), "yyyy年mm月dd日");

    await this.createView();
    return;
  }

  /**
   * ### 画面情報作成
   */
  private async createView(): Promise<void> {
    this.isLoading = true;

    const fields: Array<string> = this.createFields();
    this.fields = fields;
    const otherOfficeRoomSchedules: Array<OtherOfficeRoomSchedulesGetModel> = await this.getOtherOfficeRoomSchedules();
    const items: Array<RoomScheduleTableModel> = this.initializeOtherOfficeRoomSchedules(otherOfficeRoomSchedules);
    this.otherOfficeRoomSchedules = items;

    this.isLoading = false;

    return;
  }

  /**
   * ### テーブル列名を作成する
   *
   * - 日付の列は月毎に可変なので、当月の最終日分のfieldを作成する
   */
  private createFields(): Array<string> {
    const fields: Array<string> = [];
    // 当月の最終日を取得する。「来月の0日」を指定すると「当月の最終日」のDateを作れる
    const lastDay: number = new Date(this.year as number, this.month as number, 0).getDate();
    for (let i = 1; i <= lastDay; i++) {
      fields.push(`${this.year}/${this.month}/${i}`);
    }
    return fields;
  }

  /**
   * ### 仮押さえ／転床情報を取得する
   *
   * - 過去は表示対象外にするため、開始日時を指定する
   * - ページングを考慮し、必要数分だけ取得する
   */
  private async getOtherOfficeRoomSchedules(): Promise<Array<OtherOfficeRoomSchedulesGetModel>> {
    const responce: Array<OtherOfficeRoomSchedulesGetModel> = await this.otherOfficeRoomSchedulesService.gets(userStore, this.year, this.month);
    return responce;
  }

  private initializeOtherOfficeRoomSchedules(otherOfficeRoomSchedules: Array<OtherOfficeRoomSchedulesGetModel>): Array<RoomScheduleTableModel> {
    const items: Array<RoomScheduleTableModel> = [];
    for (const otherOfficeRoomSchedule of otherOfficeRoomSchedules) {
      const item: RoomScheduleTableModel = {
        ...otherOfficeRoomSchedule,
        checked: false
      };
      items.push(item);
    }
    return items;
  }

  /**
   * ### 前の月ボタン押下イベント
   *
   * - 画面表示中の年、月を使用してDateを作成
   * - Dateを前の月に変更する
   * - 変更した年・月を画面表示する
   */
  async onPreviousMonth(): Promise<void> {
    let currentDate: Date = new Date(this.year, this.month - 1, 1); // monthは0始まりなので-1して使用する
    currentDate.setMonth(currentDate.getMonth() - 1);
    this.year = currentDate.getFullYear();
    this.month = currentDate.getMonth() + 1;
    await this.createView();
    return;
  }

  /**
   * ### 今月ボタン押下イベント
   *
   * - 現在のDateを作成
   * - 現在の年・月を画面表示する
   */
  async onCurrentMonth(): Promise<void> {
    let toDay: Date = new Date();
    this.year = toDay.getFullYear();
    this.month = toDay.getMonth() + 1;
    await this.createView();
    return;
  }

  /**
   * ### 次の月押下イベント
   *
   * - 画面表示中の年、月を使用してDateを作成
   * - Dateを次の月に変更する
   * - 変更した年・月を画面表示する
   */
  async onNextMonth(): Promise<void> {
    let currentDate: Date = new Date(this.year, this.month - 1, 1); // monthは0始まりなので-1して使用する
    currentDate.setMonth(currentDate.getMonth() + 1);
    this.year = currentDate.getFullYear();
    this.month = currentDate.getMonth() + 1;
    await this.createView();
    return;
  }

  /**
   * ### 空床有り施設のみの日付入力モーダルの日付入力イベント
   */
  async inputOnlyBlankRoomDate(): Promise<void> {
    // カレンダー入力されたyyyy-mm-ddの日付を、表示用の日時に変換する
    this.onlyBlankRoomDate = this.$dateUtil.convertDateToString(new Date(this.filter.onlyBlankRoom.date), "yyyy年mm月dd日");

    // カレンダー入力された年月が、現在表示している年月と異なる場合は表示対象の情報を再取得する
    const year: number = this.$dateUtil.convertDateToNumber(new Date(this.filter.onlyBlankRoom.date), "yyyy");
    const month: number = this.$dateUtil.convertDateToNumber(new Date(this.filter.onlyBlankRoom.date), "mm");
    if (this.year != year || this.month != month) {
      this.year = year;
      this.month = month;

      await this.createView();
    }

    return;
  }

  /**
   * ### 全て選択押下イベント
   */
  changeAllSelect(): void {
    for (const item of this.otherOfficeRoomSchedules) {
      if (item.id == userStore.facility.id) {
        continue;
      }
      item.checked = this.allSelect;
    }

    // 選択施設が更新されるので、selectedFacilitiesを更新する
    this.updateSelectedFacilities();

    return;
  }

  /** ### otherOfficeRoomSchedulesの中でchecked=truのものをリストアップして、selectedFacilitiesを更新する */
  private updateSelectedFacilities(): void {
    const selectedFacilities: Array<{ facilityId: number; facilityName: string }> = [];
    for (const item of this.otherOfficeRoomSchedules) {
      if (item.checked) {
        selectedFacilities.push({ facilityId: item.id, facilityName: item.name });
      }
    }
    this.$emit("update:selectedFacilities", selectedFacilities);
    return;
  }

  /**
   * ### 子コンポーネントで施設のチェックボックスの値変更イベント
   *
   * - selectedFacilitiesを更新する
   */
  changeFacilityCheck(): void {
    this.updateSelectedFacilities();
    return;
  }

  /**
   * ### チェックの入力情報を初期化する
   */
  public initializeChecked(): void {
    for (const item of this.otherOfficeRoomSchedules) {
      item.checked = false;
    }
    return;
  }
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";

.room-schedule-tab {
  width: 100%;
  margin-left: auto;
  margin-right: auto;

  .current-date {
    font-weight: bold;
    font-size: 22px;
  }

  .basic-filters {
    border-radius: 3px;
    border: solid 1px rgba(228, 228, 228, 1);

    .basic-filter {
      background-color: #f5f5f5;
      border-radius: 3px;
      padding: 0.4rem;

      .table-option-text {
        font-size: 14px;
      }

      // 空床有り施設のみの日付入力
      .only-blank-room-date {
        width: 170px;
        min-width: 170px;
        background-color: #fff;
        cursor: pointer;
      }
    }
  }
}
</style>
